/**
 * Shared SQL/schema helpers for core tables and schema-derived validators.
 *
 * Invariants:
 * - Timestamp columns are represented as epoch-millisecond numbers at the app boundary.
 * - Drizzle timestamp writes must pass ISO strings via helpers in this module.
 *
 * @see `packages/core/docs/database.md`
 */
import { sql } from "drizzle-orm";
import {
  check,
  customType,
  type PgColumn,
  text,
  uuid,
  timestamp,
  type PgTable,
} from "drizzle-orm/pg-core";
import {
  createInsertSchema as drizzleCreateInsertSchema,
  createUpdateSchema as drizzleCreateUpdateSchema,
  createSelectSchema as drizzleCreateSelectSchema,
} from "drizzle-zod";
import { z } from "zod/v4";

export const id = {
  get id() {
    return uuid("id").primaryKey();
  },
};

export const sortIndex = {
  get sortIndex() {
    return text("sort_index").notNull();
  },
};

/**
 * Timestamp columns are typed as `number` for Zero compatibility, but Drizzle
 * actually stores/retrieves ISO strings. Use `nowTimestamp()` or `toTimestamp()`
 * when inserting via Drizzle to handle the type mismatch.
 */
export const utc = (name: string) =>
  timestamp(name, {
    withTimezone: true,
    mode: "string",
    precision: 0,
  }).$type<number>();

const temporalInstantColumnType = customType<{
  data: Temporal.Instant;
  driverData: string;
}>({
  dataType() {
    return "timestamp (0) with time zone";
  },
  toDriver(value) {
    return value.toString();
  },
  fromDriver(value) {
    if (/^\d{4}-\d{2}-\d{2} /.test(value) && !/[zZ]|[+-]\d{2}(?::?\d{2})?$/.test(value)) {
      return Temporal.Instant.from(`${value.replace(" ", "T")}Z`);
    }
    return Temporal.Instant.from(value);
  },
});

const temporalDateColumnType = customType<{
  data: Temporal.PlainDate;
  driverData: string;
}>({
  dataType() {
    return "date";
  },
  toDriver(value) {
    return value.toString();
  },
  fromDriver(value) {
    return Temporal.PlainDate.from(value);
  },
});

const temporalTimeColumnType = customType<{
  data: Temporal.PlainTime;
  driverData: string;
}>({
  dataType() {
    return "time";
  },
  toDriver(value) {
    return value.toString();
  },
  fromDriver(value) {
    return Temporal.PlainTime.from(value);
  },
});

export const temporalInstant = (name: string) => temporalInstantColumnType(name);

export const temporalDate = (name: string) => temporalDateColumnType(name);

export const temporalTime = (name: string) => temporalTimeColumnType(name);

/**
 * Returns current time for Drizzle timestamp columns.
 *
 * IMPORTANT:
 * - Use this for direct Drizzle/Postgres writes (i.e. using `Db`), where the
 *   runtime value must be an ISO timestamp string.
 * - Do NOT use this inside Zero transactions (`tx.mutate.*`). Zero expects
 *   timestamps as epoch-millisecond numbers for SQLite compatibility.
 *
 * This function returns an ISO string but is cast to `number` to satisfy the
 * column types produced by `utc()`.
 */
export const nowTimestamp = (): number => Temporal.Now.instant().toString() as unknown as number;

/**
 * Converts epoch milliseconds to a Drizzle timestamp value.
 *
 * Use when you have an epoch-millisecond number (e.g. from Zero) and need to
 * write it via direct Drizzle/Postgres code.
 */
export const toTimestamp = (epochMs: number): number =>
  Temporal.Instant.fromEpochMilliseconds(epochMs).toString() as unknown as number;

export const timestamps = {
  get timeCreated() {
    return utc("time_created").notNull();
  },
  get timeUpdated() {
    return utc("time_updated").notNull();
  },
  get timeArchived() {
    return utc("time_archived");
  },
};

export const temporalTimestamps = {
  get timeCreated() {
    return temporalInstant("time_created").notNull();
  },
  get timeUpdated() {
    return temporalInstant("time_updated").notNull();
  },
  get timeArchived() {
    return temporalInstant("time_archived");
  },
};

// const TIMESTAMP_COLUMNS = Object.keys(timestamps);

export const createInsertSchema = <TTable extends PgTable>(table: TTable) => {
  const schema = drizzleCreateInsertSchema(table);
  return schema.omit({
    ...("timeCreated" in table ? { timeCreated: true } : {}),
    ...("timeUpdated" in table ? { timeUpdated: true } : {}),
    ...("timeArchived" in table ? { timeArchived: true } : {}),
  });
};

export const createUpdateSchema = <TTable extends PgTable>(table: TTable) => {
  const schema = drizzleCreateUpdateSchema(table);
  return schema.omit({
    ...("timeCreated" in table ? { timeCreated: true } : {}),
    ...("timeUpdated" in table ? { timeUpdated: true } : {}),
    ...("timeArchived" in table ? { timeArchived: true } : {}),
  });
};

export const createSelectSchema = <TTable extends PgTable>(table: TTable) => {
  return drizzleCreateSelectSchema(table);
};

export const timestampsSchema = () =>
  z.object({
    timeCreated: z.number(),
    timeUpdated: z.number(),
    timeArchived: z.number().nullable(),
  }).shape;

// function timestampsAsNumbers(table: PgTable) {
//   const extend: Record<string, z.ZodType> = {};
//   for (const column of Object.values(getTableColumns(table))) {
//     if (TIMESTAMP_COLUMNS.includes(column.name)) {
//       continue;
//     }
//     if (column.columnType === "PgTimestampString") {
//       extend[column.name] = z.number();
//     }
//   }
//   return extend;
// }

export const timeAtAndZone = {
  get timeAt() {
    return utc("time_at").notNull();
  },
  get timeZone() {
    return text("time_zone").notNull();
  },
};

export const timeStartEndAndZone = {
  get timeStart() {
    return utc("time_start").notNull();
  },
  get timeEnd() {
    return utc("time_end").notNull();
  },
  get timeZone() {
    return text("time_zone").notNull();
  },
};

export const exactlyOneOf = (
  name: string,
  columns: readonly [first: PgColumn, second: PgColumn, ...rest: PgColumn[]],
) => {
  const clauses = columns.map((column, index) => {
    const otherColumns = columns.filter((_, otherIndex) => otherIndex !== index);
    const othersAreNull = otherColumns
      .map((otherColumn) => sql`${otherColumn} IS NULL`)
      .reduce((left, right) => sql`${left} AND ${right}`);
    return sql`(${column} IS NOT NULL AND ${othersAreNull})`;
  });

  return check(
    name,
    clauses.reduce((left, right) => sql`${left} OR ${right}`),
  );
};

export const equalsOneOf = (
  name: string,
  {
    value,
    columns,
  }: {
    value: PgColumn;
    columns: readonly [first: PgColumn, ...rest: PgColumn[]];
  },
) => {
  const clauses = columns.map((column) => sql`(${column} IS NOT NULL AND ${value} = ${column})`);
  return check(
    name,
    clauses.reduce((left, right) => sql`${left} OR ${right}`),
  );
};

export const atLeastOneOf = (name: string, [col1, col2]: [col1: PgColumn, col2: PgColumn]) =>
  check(name, sql`${col1} IS NOT NULL OR ${col2} IS NOT NULL`);

export const allOrNone = (name: string, columns: PgColumn[]) => {
  if (columns.length === 0) {
    throw new Error("allOrNone: At least one column is required");
  }
  const isNotNullExpr = columns
    .map((col) => sql`${col} IS NOT NULL`)
    .reduce((a, b) => sql`${a} AND ${b}`);
  const isNullExpr = columns.map((col) => sql`${col} IS NULL`).reduce((a, b) => sql`${a} AND ${b}`);
  return check(name, sql`(${isNotNullExpr}) OR (${isNullExpr})`);
};

export const dependsOn = (
  name: string,
  {
    depends,
    on,
  }: {
    depends: PgColumn;
    on: PgColumn;
  },
) => check(name, sql`${on} IS NOT NULL OR ${depends} IS NULL`);

/**
 * Escapes special characters in a string for use in SQL LIKE/ILIKE patterns.
 * Prevents `%` and `_` from being interpreted as wildcards.
 */
export function escapeLikePattern(str: string): string {
  return str.replace(/[%_\\]/g, "\\$&");
}
